import { createFileRoute, redirect } from "@tanstack/react-router";

import { BuilderPage } from "~/components/builder/builder-page";
import {
  BuilderPageMetadataSchema,
  MIGRATED_BUILDER_PAGE_REDIRECTS,
  NOINDEX_BUILDER_PAGE_NAMES,
  buildBuilderPageMeta,
  loadBuilderPageContent,
} from "~/lib/builder/page";
import { BRAND_URL } from "~/lib/shared/brand";
import { clientEnv } from "~/lib/utils/client-env";

const BuilderCatchAllPage = () => (
  <BuilderPage content={Route.useLoaderData()} />
);

export const Route = createFileRoute("/_public/page/$name/")({
  staleTime: 6 * 60 * 60 * 1000, // 6 hours
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ params, deps }) => {
    const migratedTo = MIGRATED_BUILDER_PAGE_REDIRECTS[params.name];
    if (migratedTo) {
      throw redirect({ to: migratedTo, statusCode: 301 });
    }
    return loadBuilderPageContent({
      urlPath: params.name ? `/page/${params.name}` : "/",
      search: deps.search,
    });
  },
  head: ({ loaderData, params }) => {
    const parsed = BuilderPageMetadataSchema.safeParse(loaderData);
    const urlValue = parsed.success ? parsed.data?.data?.url : undefined;
    // Entries targeted at multiple URLs store an array; the /page/* one is
    // the canonical home for this catch-all.
    const url = Array.isArray(urlValue)
      ? (urlValue.find((u) => u.startsWith("/page/")) ?? urlValue[0])
      : urlValue;
    const slug =
      url
        ?.split("/")
        .filter((segment) => segment !== "" && segment !== "page")
        .join("/") || "";
    const canonicalUrl = `${BRAND_URL}/page/${slug}`.replace(/\/$/, "");

    return buildBuilderPageMeta({
      content: loaderData,
      canonicalUrl,
      noindexFollow: NOINDEX_BUILDER_PAGE_NAMES.has(params.name),
    });
  },
  component: BuilderCatchAllPage,
});

// Static generation for TanStack Start
export const generateStaticParams = async () => {
  const { fetchEntries } = await import("@builder.io/sdk-react/edge");

  const pages = await fetchEntries({
    model: "page",
    apiKey: clientEnv.VITE_BUILDER_API_KEY,
    omit: "data.blocks",
  });

  // Only entries targeted at /page/* belong to this catch-all. Entries that
  // live solely at a clean URL (e.g. /how-it-works) render via their own shim
  // route — emitting them here would prerender junk 404s under /page/*.
  return pages
    .filter((a) => !!a.data)
    .flatMap((page) => {
      const urlValue: unknown = page.data?.url;
      // Entries targeted at multiple URLs store an array of paths.
      const url = Array.isArray(urlValue)
        ? urlValue.find(
            (u): u is string => typeof u === "string" && u.startsWith("/page/"),
          )
        : urlValue;
      if (typeof url !== "string" || !url.startsWith("/page/")) return [];
      return [
        {
          path: url
            .replace(/^\/page\/?/, "")
            .split("/")
            .filter(Boolean),
        },
      ];
    });
};
